// Imports
import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import uniqueId from 'lodash/uniqueId';
import { ThemeProvider } from 'styled-components/macro';
import useUpdateOnlyEffect from '../../../../../../shareables/foundation/front-end/utils/use-update-only-effect';
import saveValue from './save-value';
import FieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/field-container';
import { Label } from '../../../../../../shareables/foundation/front-end/components/forms/labels';
import ControlGroup from '../../../../../../shareables/foundation/front-end/components/forms/control-group';
import AddOn from '../../../../../../shareables/foundation/front-end/components/forms/add-on';
import InputContainer from '../../../../../../shareables/foundation/front-end/components/forms/input-container';
import FieldErrorMessage from '../../../../../../shareables/foundation/front-end/components/forms/field-error-message';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.min.css';
import 'intl-tel-input/build/js/utils.js';
import APIConnectedFormContext, { ContextDetails } from '../../../../../contexts/api-connected-form';
import debounce from 'lodash/debounce';
import { z } from 'zod';
import parsePersistentFormValue from './utils/parse-persistent-form-value';
import BaseFieldProps from './base-field-props';


// Define the accepted props
interface Props extends BaseFieldProps {
	/** The initial value state of the field. Will be ignored on subsequent renders. */
	initialValue?: string;
	
	
	/** Text that will be displayed before the field to augment what kind of value the field provides. */
	prefix?: string;
	
	
	/** Text that will be displayed after the field to augment what kind of value the field provides. */
	suffix?: string;
	
	
	/** Countries that will appear at the top of the country select dropdown. Must be a list of two letter country codes. Defaults to `['US']`. */
	preferredTelCountries?: string[];
	
	
	/** A function that handles a change in value to the field. The function is passed the latest value as its parameter. */
	onChange?: (newValue: string) => void;
	
	
	/** A function that provides the getter for the field to be bound, usually to a `ref`. */
	bindGetter?: (getter: () => string) => void;
	
	
	/** A function that provides the setter for the field to be bound, usually to a `ref`. */
	bindSetter?: (setter: (value: string) => void) => void;
}

type CompleteProps = Props &
	Omit<
		React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
		'id' | 'className' | 'value' | 'defaultValue' | 'type' | 'checked' | 'defaultChecked' | 'prefix' | 'onChange'
	>;


// Function component
const Phone = React.forwardRef<HTMLInputElement, CompleteProps>((props: CompleteProps, ref) => {
	// Break off the props we don't want to give to the intrinsic element
	const {
		initialValue,
		label,
		note,
		prefix,
		suffix,
		preferredTelCountries,
		errorMessage: errorMessageProp,
		onChange: onChangeProp,
		bindGetter,
		bindSetter,
		recoverState: recoverStateProp,
		exclude: excludeProp,
		...passThroughProps
	} = props;
	
	
	// Use context
	const context = useContext(APIConnectedFormContext);
	
	
	// Retrieve values from API form's context, falling back to props
	const recoverState = recoverStateProp ?? context?.recoverState;
	const errorMessage = context?.error.field === passThroughProps.name ? context.error.message : errorMessageProp;
	
	
	// Decide if the field is excluded
	const exclude = excludeProp ?? passThroughProps.disabled ?? undefined;
	
	
	// Use state
	const [nationalValue, setNationalValue] = useState('');
	const [internationalValue, setInternationalValue] = useState<string>(initialValue || '');
	
	
	// Use refs
	const phoneInputElement = useRef<HTMLInputElement | null>(null);
	const intlTelInputInstance = useRef<intlTelInput.Plugin | null>(null);
	const instanceFinishedInitializing = useRef<boolean>(false);
	
	
	// Cleans up an international numbers
	const cleanInternationalNumber = (number: string) => {
		// Strip unwanted characters
		number = number.replace(/[^+0-9x]/g, '');
		
		
		// Handle if country code is missing
		if (number[0] !== '+') {
			// Get country data
			const countryData = intlTelInputInstance.current?.getSelectedCountryData();
			
			
			// Add country code
			if (countryData) {
				number = `+${countryData.dialCode}${number}`;
			} else {
				number = `+1${number}`;
			}
		}
		
		
		// Return result
		return number;
	};
	
	
	// Helper function that gets the current value
	const getValue = useCallback(() => {
		// Get country code
		const countryCode = `+${intlTelInputInstance.current?.getSelectedCountryData().dialCode ?? 1}`;
		
		
		// If the phone number is just the country code, return nothing
		if (internationalValue === countryCode) {
			return '';
		}
		
		
		// Return international number
		return internationalValue;
	}, [internationalValue]);
	
	
	// Helper function that sets the current value
	const setValue = useCallback((value: string) => {
		intlTelInputInstance.current?.setNumber(value);
		
		setNationalValue(intlTelInputInstance.current?.getNumber(window.intlTelInputUtils.numberFormat.NATIONAL) || value);
		
		setInternationalValue(
			cleanInternationalNumber(
				intlTelInputInstance.current?.getNumber(window.intlTelInputUtils?.numberFormat?.INTERNATIONAL) || value
			)
		);
	}, []);
	
	
	// Bind getter/setter
	bindGetter && bindGetter(getValue);
	bindSetter && bindSetter(setValue);
	
	
	// Add and remove getter from connected form
	const { name } = passThroughProps;
	let addValueGetter: NonNullable<ContextDetails>['addValueGetter'] | null = null;
	let removeValueGetter: NonNullable<ContextDetails>['removeValueGetter'] | null = null;
	
	if (context) {
		addValueGetter = context.addValueGetter;
		removeValueGetter = context.removeValueGetter;
	}
	
	useEffect(() => {
		if (addValueGetter === null || exclude === true) {
			return;
		}
		
		addValueGetter(name, getValue);
		
		return () => {
			if (removeValueGetter === null) {
				return;
			}
			
			removeValueGetter(name);
		};
	}, [name, getValue, addValueGetter, removeValueGetter, exclude]);
	
	
	// Generate unique ID
	const uniqueID = useRef(uniqueId('phone-field-'));
	
	
	// Handle value changes
	const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
		setNationalValue(event.target.value);
		setInternationalValue(
			cleanInternationalNumber(
				intlTelInputInstance.current?.getNumber(window.intlTelInputUtils?.numberFormat?.INTERNATIONAL) ||
					event.target.value
			)
		);
	};
	
	
	// Debounce save value function
	const debouncedSaveValue = useCallback(debounce(saveValue, 300), []); // eslint-disable-line react-hooks/exhaustive-deps
	
	
	// Save new value on updates
	useUpdateOnlyEffect(() => {
		recoverState &&
			recoverState.enabled &&
			debouncedSaveValue(passThroughProps.name, internationalValue, recoverState.uniqueIdentifier);
	}, [internationalValue]);
	
	
	// Update state based with current values
	const updateState = useCallback(() => {
		// Do nothing if instance is not present
		if (!intlTelInputInstance.current) {
			return;
		}
		
		
		// Determine new values
		let newNationalValue = intlTelInputInstance.current.getNumber(window.intlTelInputUtils?.numberFormat?.NATIONAL);
		let newInternationalValue = cleanInternationalNumber(
			intlTelInputInstance.current.getNumber(window.intlTelInputUtils?.numberFormat?.INTERNATIONAL)
		);
		
		if (!instanceFinishedInitializing.current && phoneInputElement.current) {
			newNationalValue = phoneInputElement.current.value;
			newInternationalValue = cleanInternationalNumber(phoneInputElement.current.value);
		}
		
		
		// Only update if they differ
		if (newNationalValue !== nationalValue) {
			setNationalValue(newNationalValue);
		}
		
		if (newInternationalValue !== internationalValue) {
			setInternationalValue(newInternationalValue);
		}
	}, [internationalValue, nationalValue]);
	
	
	// Handle storing the single element in our internal ref and the forwarded ref
	const handleRef = useCallback(
		(instance: HTMLInputElement) => {
			// Store in our internal ref
			phoneInputElement.current = instance;
			
			
			// Store in the forwarded ref (if provided)
			if (ref) {
				if (typeof ref === 'function') {
					ref(instance);
				} else {
					ref.current = instance;
				}
			}
		},
		[ref]
	);
	
	
	// Handle construction / deconstruction
	useEffect(() => {
		// Only handle if phone input element is available
		if (phoneInputElement.current) {
			// Dispatch a window load event
			//  -> International Telephone Input adds an event listener for the "load" event, and waits until that's been fired to fully initialize.
			//     However, since we're using React's code-splitting, the load event often fires before ITI can add its listener.
			//     This means that ITI will hang indefinitely and never fully initialize.
			dispatchEvent(new Event('load'));
			
			
			// Initialize International Telephone Input
			intlTelInputInstance.current = intlTelInput(phoneInputElement.current, {
				preferredCountries: preferredTelCountries ?? ['us'],
				autoPlaceholder: passThroughProps.disabled ? 'off' : 'polite',
			});
		}
		
		
		// Return cleanup function
		return () => {
			intlTelInputInstance.current?.destroy();
		};
	}, [passThroughProps.disabled, preferredTelCountries]);
	
	
	// Handle initial value
	useEffect(() => {
		// Wait for it to be ready
		intlTelInputInstance.current?.promise
			.then(() => {
				// Remember that the promise was fulfilled
				instanceFinishedInitializing.current = true;
				
				
				// Handle any initial value
				if (initialValue) {
					setValue(initialValue);
				}
			})
			.catch((reason: unknown) => {
				throw new Error(typeof reason === 'string' ? reason : 'Unknown error');
			});
	}, [initialValue, setValue]);
	
	
	// Add and remove country change event handler
	useEffect(() => {
		// Wait for it to be ready
		intlTelInputInstance.current?.promise
			.then(() => {
				// Listen for country changes
				phoneInputElement.current?.addEventListener('countrychange', updateState);
			})
			.catch((reason: unknown) => {
				throw new Error(typeof reason === 'string' ? reason : 'Unknown error');
			});
		
		
		// Return cleanup function
		return () => {
			phoneInputElement.current?.removeEventListener('countrychange', updateState);
		};
	}, [updateState]);
	
	
	// Notify parent of changes
	useUpdateOnlyEffect(() => {
		onChangeProp?.(internationalValue);
		context?.onChange?.();
	}, [internationalValue]);
	
	useUpdateOnlyEffect(() => {
		context?.onExcludeChange?.();
	}, [exclude]);
	
	
	// Use ref
	const hasMounted = useRef(false);
	
	
	// Attempt to recover state on mount
	if (recoverState && recoverState.enabled && hasMounted.current === false) {
		hasMounted.current = true;
		
		window.setTimeout(() => {
			// Attempt to recover value
			const recoveredValue = parsePersistentFormValue(recoverState.uniqueIdentifier, passThroughProps.name, z.string());
			
			
			// Wait for it to be ready
			intlTelInputInstance.current?.promise
				.then(() => {
					// Remember that the promise was fulfilled
					instanceFinishedInitializing.current = true;
					
					
					// Handle any initial value
					if (typeof recoveredValue !== 'undefined') {
						intlTelInputInstance.current?.setNumber(recoveredValue);
						setNationalValue(
							intlTelInputInstance.current?.getNumber(window.intlTelInputUtils.numberFormat.NATIONAL) || recoveredValue
						);
						
						setInternationalValue(
							cleanInternationalNumber(
								intlTelInputInstance.current?.getNumber(window.intlTelInputUtils?.numberFormat?.INTERNATIONAL) ||
									recoveredValue
							)
						);
					}
				})
				.catch((reason: unknown) => {
					throw new Error(typeof reason === 'string' ? reason : 'Unknown error');
				});
		}, 1); // Must happen asynchronously
		
		/* Don't trigger onChange here; it will be triggered before all getters are bound, and cause issues */
	}
	
	
	// Create theme
	const theme = {
		hasError: Boolean(errorMessage),
	};
	
	
	// Return JSX
	return (
		<ThemeProvider theme={theme}>
			<FieldContainer>
				{label && (
					<Label htmlFor={uniqueID.current}>
						{label}
						{note && <small>{note}</small>}
					</Label>
				)}
				
				<ControlGroup>
					{prefix && <AddOn>{prefix}</AddOn>}
					
					<InputContainer>
						<input
							{...passThroughProps}
							id={uniqueID.current}
							value={nationalValue}
							onChange={handleChange}
							onBlur={updateState}
							ref={handleRef}
							aria-invalid={Boolean(errorMessage)}
							aria-describedby={`error-field-${uniqueID.current}`}
							spellCheck='false'
							autoCorrect='off'
						/>
					</InputContainer>
					
					{suffix && <AddOn>{suffix}</AddOn>}
				</ControlGroup>
				
				{errorMessage && <FieldErrorMessage id={`error-field-${uniqueID.current}`} message={errorMessage} />}
			</FieldContainer>
		</ThemeProvider>
	);
});


// @ts-ignore: displayName does exist
Phone.displayName = 'Phone';

export default Phone;
